/**
 * errorMessages.js — Résolution centralisée des messages d'erreur frontend.
 * Tous les messages utilisateur sont désormais lus depuis le catalogue partagé.
 */

import i18n from '../i18n';

// ---------------------------------------------------------------------------
// 1. Dictionnaire des messages d'erreur HTTP
// ---------------------------------------------------------------------------

/**
 * Objet qui fait correspondre un code de statut HTTP (nombre) a un message
 * comprehensible et orienté action pour l'utilisateur.
 *
 * Chaque message :
 *   - Decrit brievement le probleme rencontre.
 *   - Propose une action concrete (se reconnecter, reessayer, verifier le formulaire…).
 *
 * Les codes couverts sont ceux que le backend Ringo (Django REST Framework) est
 * susceptible de renvoyer le plus souvent :
 *   400 — Donnees invalides (validation de formulaire echouee)
 *   401 — Session expiree ou token invalide
 *   403 — Permission refusee (role insuffisant)
 *   404 — Ressource introuvable
 *   409 — Conflit (modification concurrente)
 *   429 — Trop de requetes (throttling DRF)
 *   500 — Erreur serveur inattendue
 *   502 — Passerelle indisponible (Nginx/Gunicorn down)
 *   503 — Service en maintenance
 */
export const HTTP_ERROR_MESSAGE_KEYS = {
  400: 'http_errors.400',
  401: 'http_errors.401',
  403: 'http_errors.403',
  404: 'http_errors.404',
  409: 'http_errors.409',
  429: 'http_errors.429',
  500: 'http_errors.500',
  502: 'http_errors.502',
  503: 'http_errors.503',
};

// ---------------------------------------------------------------------------
// 2. Message generique par defaut
// ---------------------------------------------------------------------------

/**
 * Message de repli utilise lorsque aucun message plus precis n'a pu etre
 * extrait de l'erreur et qu'aucun `fallback` n'a ete fourni par l'appelant.
 */
const DEFAULT_MESSAGE_KEY = 'http_errors.default';

// ---------------------------------------------------------------------------
// 3. Message d'erreur reseau (pas de reponse du serveur)
// ---------------------------------------------------------------------------

/**
 * Message affiche quand le navigateur n'a pas pu joindre le serveur du tout
 * (coupure reseau, DNS en echec, serveur eteint, CORS bloque, etc.).
 */
const NETWORK_ERROR_MESSAGE_KEY = 'http_errors.network';

function translate(key) {
  return i18n.t(key);
}

// ---------------------------------------------------------------------------
// 4. Fonction principale : getErrorMessage
// ---------------------------------------------------------------------------

/**
 * Extrait un message d'erreur exploitable a partir d'un objet d'erreur Axios
 * ou d'un objet Error standard.
 *
 * Strategie de resolution (dans l'ordre de priorite) :
 *
 *   a) Erreur reseau :
 *      - Si `error.code` vaut `'ERR_NETWORK'` (constante Axios pour une
 *        requete qui n'a jamais recu de reponse), ou si `error.response` est
 *        absent, on renvoie le message reseau specifique.
 *
 *   b) Message explicite du backend :
 *      - Le backend Django REST Framework peut renvoyer le message d'erreur
 *        dans differents champs du corps JSON de la reponse :
 *          * `error.response.data.error`   — format personnalise Ringo
 *          * `error.response.data.detail`  — format DRF standard
 *          * `error.response.data.message` — format alternatif courant
 *      - On prend le premier de ces champs qui contient une chaine non vide.
 *
 *   c) Code HTTP connu :
 *      - Si aucun message explicite n'a ete trouve dans le corps, on cherche
 *        le code de statut dans le dictionnaire `HTTP_ERROR_MESSAGES`.
 *
 *   d) Fallback de l'appelant :
 *      - Si un parametre `fallback` a ete fourni, on l'utilise.
 *
 *   e) Message generique :
 *      - En dernier recours, on renvoie `DEFAULT_MESSAGE`.
 *
 * @param {Error|Object} error    — L'erreur capturee (typiquement dans un catch
 *                                   Axios ou un try/catch). Peut etre un objet
 *                                   Axios AxiosError, un Error natif, ou meme
 *                                   un objet quelconque.
 * @param {string}       [fallback] — Message de repli optionnel fourni par
 *                                     l'appelant, utilise si aucune information
 *                                     plus precise n'est disponible.
 * @returns {string} Un message d'erreur lisible et exploitable par l'utilisateur.
 */
export function getErrorMessage(error, fallback) {
  // -----------------------------------------------------------------------
  // a) Detection d'une erreur reseau (aucune reponse recue du serveur)
  // -----------------------------------------------------------------------
  // Axios positionne `error.code` a 'ERR_NETWORK' quand la requete echoue
  // avant meme de recevoir une reponse HTTP (timeout, DNS, CORS, etc.).
  // On detecte aussi le cas ou `error.response` est tout simplement absent,
  // ce qui arrive quand la requete n'a jamais abouti.
  if (error?.code === "ERR_NETWORK" || (error && !error.response)) {
    return translate(NETWORK_ERROR_MESSAGE_KEY);
  }

  // -----------------------------------------------------------------------
  // b) Extraction du message explicite depuis le corps de la reponse
  // -----------------------------------------------------------------------
  // Le backend peut renvoyer l'erreur dans plusieurs formats JSON differents.
  // On tente chaque champ dans l'ordre de priorite du projet Ringo :
  //   1. `error`   — champ personnalise utilise dans nos vues Django
  //   2. `detail`  — champ standard de Django REST Framework (ex: 403, 401)
  //   3. `message` — champ alternatif parfois utilise par des serializers tiers
  const responseData = error?.response?.data;

  if (responseData) {
    // On verifie que la valeur est bien une chaine non vide avant de l'utiliser.
    // Cela evite de renvoyer un objet ou un tableau par erreur.
    const backendMessage =
      (typeof responseData.error === "string" && responseData.error) ||
      (typeof responseData.detail === "string" && responseData.detail) ||
      (typeof responseData.message === "string" && responseData.message);

    if (backendMessage) {
      return backendMessage;
    }
  }

  // -----------------------------------------------------------------------
  // c) Recherche du code HTTP dans le dictionnaire des messages connus
  // -----------------------------------------------------------------------
  // Si le backend n'a pas fourni de message textuel (ou si le corps est vide /
  // dans un format inattendu), on se rabat sur le code de statut HTTP.
  const status = error?.response?.status;

  if (status && HTTP_ERROR_MESSAGE_KEYS[status]) {
    return translate(HTTP_ERROR_MESSAGE_KEYS[status]);
  }

  // -----------------------------------------------------------------------
  // d) & e) Fallback de l'appelant ou message generique
  // -----------------------------------------------------------------------
  // Si ni le corps de la reponse ni le code HTTP n'ont permis de determiner
  // un message utile, on utilise le fallback fourni par l'appelant (s'il
  // existe), sinon le message generique par defaut.
  return fallback || translate(DEFAULT_MESSAGE_KEY);
}
